import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { publicAPI, getWebsiteImageUrl } from '../services/api.jsx';
import { useTranslation } from '../contexts/TranslationContext';

const Home = () => {
    const { t } = useTranslation();
    const [heroImage, setHeroImage] = useState('/Solarpanele.webp');

    useEffect(() => {
        fetchHeroImage();
    }, []);

    const fetchHeroImage = async () => {
        try {
            const response = await publicAPI.getWebsiteImage('hero_background');
            if (response.data.image_path) {
                const imageUrl = getWebsiteImageUrl(response.data.image_path);
                console.log('Setting hero image URL:', imageUrl);
                setHeroImage(imageUrl);
            }
        } catch (error) {
            console.error('Error fetching hero image:', error);
        }
    };

    const scrollToBenefits = () => {
        const benefitsSection = document.getElementById('benefits-section');
        if (benefitsSection) {
            window.scrollTo({
                top: benefitsSection.offsetTop,
                behavior: 'smooth'
            });
        }
    };

    const workflowSteps = [
        {
            step: "01",
            title: t('home.step1Title'),
            description: t('home.step1Desc')
        },
        {
            step: "02",
            title: t('home.step2Title'),
            description: t('home.step2Desc')
        },
        {
            step: "03",
            title: t('home.step3Title'),
            description: t('home.step3Desc')
        },
        {
            step: "04",
            title: t('home.step4Title'),
            description: t('home.step4Desc')
        }
    ];

    return (
        <div className="min-h-screen bg-white">
            {/* Hero Section */}
            <section className="relative min-h-screen flex items-center overflow-hidden">
                <div className="absolute inset-0">
                    <img
                        src={heroImage}
                        alt="Solar Panels"
                        className="w-full h-full object-cover"
                        onError={(e) => {
                            e.target.src = "/Solarpanele.webp";
                        }}
                    />
                </div>

                <div className="absolute inset-0 bg-black/50"></div>

                <div className="relative container-custom py-20 text-center">
                    <div className="max-w-4xl mx-auto space-y-8">
                        <h1 className="text-4xl sm:text-5xl lg:text-8xl xl:text-9xl font-bold text-white leading-tight">
                            {t('home.heroTitle')}
                        </h1>

                        <p className="text-2xl lg:text-3xl text-gray-200 leading-relaxed max-w-3xl mx-auto">
                            {t('home.heroDescription')}
                        </p>

                        <div className="flex flex-col sm:flex-row gap-4 justify-center">
                            <Link
                                to="/contact"
                                className="inline-flex items-center justify-center px-8 py-4 border-2 border-white text-white font-semibold rounded-lg hover:bg-white hover:text-gray-900 transition-all duration-300"
                            >
                                {t('home.freeConsultation')}
                            </Link>
                        </div>
                    </div>
                </div>

                <div className="absolute bottom-8 left-1/2 transform -translate-x-1/2">
                    <button
                        onClick={scrollToBenefits}
                        className="group flex flex-col items-center space-y-2 text-white hover:text-gray-200 transition-all duration-300 cursor-pointer"
                    >
                        <div className="bg-white/20 backdrop-blur-sm border border-white/30 rounded-full px-6 py-3 font-semibold text-sm hover:bg-white/30 transition-all duration-300">
                            {t('home.scrollToBenefits')}
                        </div>
                        <svg
                            className="w-6 h-6 animate-bounce group-hover:translate-y-1 transition-transform"
                            fill="none"
                            stroke="currentColor"
                            viewBox="0 0 24 24"
                        >
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 14l-7 7m0 0l-7-7m7 7V3" />
                        </svg>
                    </button>
                </div>
            </section>

            {/* Benefits Section */}
            <section id="benefits-section" className="py-32 bg-gray-50">
                <div className="container-custom">
                    <div className="text-center mb-20">
                        <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
                            {t('home.benefitsSubtitle')}
                        </h2>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
                        <div className="bg-white p-8 rounded-2xl border border-gray-200 hover:border-gray-300 hover:shadow-lg transition-all duration-300 text-center group">
                            <div className="w-16 h-16 bg-gray-900 rounded-xl flex items-center justify-center mx-auto mb-6 group-hover:scale-110 transition-transform duration-300">
                                <svg className="w-8 h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                                </svg>
                            </div>
                            <h3 className="text-xl font-bold text-gray-900 mb-4">{t('home.saveCosts')}</h3>
                            <p className="text-gray-600">{t('home.saveCostsDesc')}</p>
                        </div>

                        <div className="bg-white p-8 rounded-2xl border border-gray-200 hover:border-gray-300 hover:shadow-lg transition-all duration-300 text-center group">
                            <div className="w-16 h-16 bg-gray-900 rounded-xl flex items-center justify-center mx-auto mb-6 group-hover:scale-110 transition-transform duration-300">
                                <svg className="w-8 h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                                </svg>
                            </div>
                            <h3 className="text-xl font-bold text-gray-900 mb-4">{t('home.protectEnvironment')}</h3>
                            <p className="text-gray-600">{t('home.protectEnvironmentDesc')}</p>
                        </div>

                        <div className="bg-white p-8 rounded-2xl border border-gray-200 hover:border-gray-300 hover:shadow-lg transition-all duration-300 text-center group">
                            <div className="w-16 h-16 bg-gray-900 rounded-xl flex items-center justify-center mx-auto mb-6 group-hover:scale-110 transition-transform duration-300">
                                <svg className="w-8 h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 10V3L4 14h7v7l9-11h-7z" />
                                </svg>
                            </div>
                            <h3 className="text-xl font-bold text-gray-900 mb-4">{t('home.independence')}</h3>
                            <p className="text-gray-600">{t('home.independenceDesc')}</p>
                        </div>
                    </div>
                </div>
            </section>

            {/* How It Works Section */}
            <section className="py-24 bg-white">
                <div className="container-custom">
                    <div className="text-center mb-20">
                        <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
                            {t('home.howItWorksSubtitle')}
                        </h2>
                        <p className="text-xl text-gray-600 max-w-3xl mx-auto leading-relaxed">
                            {t('home.howItWorksDescription')}
                        </p>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
                        {workflowSteps.map((step, index) => (
                            <div key={index} className="relative group h-full">
                                <div className="bg-gray-50 border border-gray-200 rounded-2xl p-8 hover:bg-gray-100 hover:border-gray-300 hover:shadow-lg transition-all duration-300 h-full flex flex-col min-h-[280px]">
                                    <div className="text-6xl font-black text-gray-300 mb-6 group-hover:text-gray-400 transition-colors duration-300">{step.step}</div>
                                    <h3 className="text-xl font-bold text-gray-900 mb-4 flex-shrink-0">{step.title}</h3>
                                    <p className="text-gray-600 leading-relaxed flex-grow">{step.description}</p>
                                </div>

                                {index < 3 && (
                                    <div className="hidden lg:block absolute top-1/2 left-full w-8 h-0.5 bg-gray-300 transform -translate-y-1/2 z-10" style={{ top: '50%' }}>
                                        <div className="absolute right-0 top-1/2 transform -translate-y-1/2 translate-x-1 w-0 h-0 border-l-4 border-l-gray-300 border-t-2 border-b-2 border-t-transparent border-b-transparent"></div>
                                    </div>
                                )}
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* Gallery Section */}
            <section className="py-24 bg-gray-50">
                <div className="container-custom">
                    <div className="text-center mb-16">
                        <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
                            {t('home.galleryTitle')}
                        </h2>
                        <p className="text-xl text-gray-600 max-w-2xl mx-auto">
                            {t('home.galleryDescription')}
                        </p>
                    </div>

                    <div className="flex justify-center">
                        <Link
                            to="/gallery"
                            className="inline-flex items-center justify-center px-8 py-4 border-2 border-gray-900 text-gray-900 font-semibold rounded-lg hover:bg-gray-900 hover:text-white transition-all duration-300 group"
                        >
                            {t('home.viewGallery')}
                            <svg className="w-5 h-5 ml-2 group-hover:translate-x-1 transition-transform" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                            </svg>
                        </Link>
                    </div>
                </div>
            </section>
        </div>
    );
};

export default Home;